import React from 'react';
import { User, MapPin, Eye, Edit, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  TableCell,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ScheduleItem, Doctor, Staff, Room } from '../SchedulerTypes';
import { getDoctorName, getStaffName, getRoomName } from './utils';

interface ScheduleRowProps {
  item: ScheduleItem;
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  onViewNotes: (item: ScheduleItem) => void;
  onEditScheduleItem?: (item: ScheduleItem) => void;
  onDeleteScheduleItem?: (id: string) => void;
}

const ScheduleTableRow: React.FC<ScheduleRowProps> = ({
  item,
  doctors,
  staff,
  rooms,
  onViewNotes,
  onEditScheduleItem,
  onDeleteScheduleItem
}) => {
  return (
    <TableRow key={item.id}>
      <TableCell className="font-medium">{item.date}</TableCell>
      <TableCell className="font-medium">{item.startTime} - {item.endTime}</TableCell>
      <TableCell>{item.title}</TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="secondary">{item.type}</Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                <span className="capitalize">{item.type}</span> schedule item
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        {item.doctorId ? (
          <div className="flex items-center space-x-2">
            <User className="h-4 w-4" />
            <span>{getDoctorName(item.doctorId, doctors)}</span>
          </div>
        ) : item.staffId ? (
          <div className="flex items-center space-x-2">
            <User className="h-4 w-4" />
            <span>{getStaffName(item.staffId, staff)}</span>
          </div>
        ) : (
          'N/A'
        )}
      </TableCell>
      <TableCell>
        {item.roomId ? (
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4" />
            <span>{getRoomName(item.roomId, rooms)}</span>
          </div>
        ) : (
          'N/A'
        )}
      </TableCell>
      <TableCell>
        {item.notes ? (
          <Button 
            variant="ghost" 
            size="sm" 
            className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
            onClick={() => onViewNotes(item)}
          >
            <Eye className="h-4 w-4" />
            <span>View Notes</span>
          </Button>
        ) : (
          'N/A'
        )}
      </TableCell>
      <TableCell>
        <div className="flex space-x-2">
          {onEditScheduleItem && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onEditScheduleItem(item)}
              title="Edit schedule item"
            >
              <Edit className="h-4 w-4 text-blue-600" />
            </Button>
          )}
          {onDeleteScheduleItem && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this schedule item?')) {
                  onDeleteScheduleItem(item.id);
                }
              }}
              title="Delete schedule item"
            >
              <Trash2 className="h-4 w-4 text-red-600" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default ScheduleTableRow;
